export const pt = {
  htmlLang: 'pt-BR',
  localeName: 'Português',

  brand: {
    name: 'DPGen',
    expansion: 'Deterministic Password Generator',
    tagline: 'senhas derivadas, não guardadas',
  },

  topbar: {
    theme: 'Alternar tema',
    lang: 'Alternar idioma',
    source: 'Código-fonte no GitHub',
    skip: 'Pular para o gerador',
  },

  hero: {
    badge: 'Sem cofre · sem servidor · sem armazenamento',
    titleLead: 'A mesma senha,',
    titleAccent: 'toda vez',
    titleTail: '— sem nunca guardá-la.',
    subtitle:
      'Sua senha não é salva em lugar nenhum: ela é recalculada a partir da sua senha-mestra e do nome do serviço, usando Argon2id. Mesmas entradas, mesma senha, em qualquer aparelho.',
    ctaPrimary: 'Gerar minha senha',
    ctaSecondary: 'Como funciona',
    facts: [
      { value: 'Argon2id', label: 'derivação memory-hard, 64 MiB por tentativa' },
      { value: '0 bytes', label: 'gravados no seu aparelho ou em qualquer servidor' },
      { value: '100%', label: 'no navegador — funciona até offline' },
    ],
  },

  gen: {
    title: 'Gerador',
    subtitle: 'Tudo acontece neste aparelho. Nada é enviado, nada é salvo.',

    master: {
      label: 'Senha-mestra',
      placeholder: 'a única senha que você precisa decorar',
      hint: 'Escolha algo longo e único. Se ela vazar, todas as senhas derivadas vazam junto.',
      show: 'Mostrar senha-mestra',
      hide: 'Ocultar senha-mestra',
    },

    fingerprint: {
      label: 'Impressão digital',
      hint: 'Estes três símbolos dependem só da sua senha-mestra. Memorize-os: se mudarem, você digitou errado.',
      toggle: 'Mostrar impressão digital',
      privacy: 'Não compartilhe nem fotografe esta marca.',
    },

    service: {
      label: 'Serviço',
      placeholder: 'github.com',
      hint: 'Domínio ou nome do site. Maiúsculas e espaços são ignorados.',
    },

    login: {
      label: 'Login',
      placeholder: 'seu.usuario@exemplo.com',
      hint: 'Usuário ou e-mail da conta. Pode ficar vazio, mas então precisa ficar vazio sempre.',
    },

    counter: {
      label: 'Versão',
      hint: 'Aumente em 1 para trocar a senha deste serviço sem mudar a mestra.',
      decrease: 'Diminuir versão',
      increase: 'Aumentar versão',
    },

    preset: {
      label: 'Formato',
      custom: 'Personalizado',
      options: {
        compat: 'Compatível',
        standard: 'Padrão',
        long: 'Longa',
        pin: 'PIN',
        lowercase: 'Só letras',
      },
      descriptions: {
        compat: '12 caracteres, sem símbolos — para sites exigentes',
        standard: '16 caracteres com símbolos — bom para quase tudo',
        long: '32 caracteres — máxima resistência',
        pin: '6 dígitos — cadeados, cartões, telas de bloqueio',
        lowercase: '20 letras minúsculas — fácil de digitar na TV',
      },
    },

    advanced: {
      show: 'Opções avançadas',
      hide: 'Ocultar opções avançadas',
      warning:
        'Qualquer mudança aqui gera uma senha completamente diferente. Anote o que escolheu.',
    },

    length: { label: 'Comprimento', unit: (n: number) => `${n} caracteres` },

    classes: {
      label: 'Caracteres permitidos',
      lower: 'Minúsculas',
      upper: 'Maiúsculas',
      digits: 'Números',
      symbols: 'Símbolos',
      atLeastOne: 'Selecione pelo menos um conjunto.',
    },

    symbolSet: {
      label: 'Conjunto de símbolos',
      safe: 'Seguro',
      extended: 'Estendido',
      safeHint: '!#$%&()*+,-./:;<=>?@[]^_{|}~ — aceito por praticamente todo site',
      extendedHint: 'inclui aspas, crase, barra invertida e espaço — alguns sites rejeitam',
    },

    ambiguous: {
      label: 'Evitar caracteres ambíguos',
      hint: 'Remove O 0 o I l 1 | — útil quando você precisa ler ou ditar a senha.',
    },

    cost: {
      label: 'Custo de derivação',
      fast: 'Rápido',
      balanced: 'Equilibrado',
      high: 'Alto',
      hints: {
        fast: '32 MiB · 2 passagens — aparelhos antigos',
        balanced: '64 MiB · 3 passagens — recomendado',
        high: '256 MiB · 4 passagens — pode falhar em celulares',
      },
    },

    submit: 'Gerar senha',
    working: 'Derivando…',
    workingNote: 'O cálculo é lento de propósito: é isso que trava quem tenta adivinhar.',
    clear: 'Limpar tudo',

    validation: {
      masterRequired: 'Informe a senha-mestra.',
      serviceRequired: 'Informe o serviço.',
    },

    error: {
      title: 'Não foi possível gerar',
      memory:
        'Seu navegador não conseguiu reservar a memória do perfil escolhido. Tente o perfil Equilibrado ou Rápido — lembrando que isso muda a senha gerada.',
      wasm:
        'O módulo WebAssembly do Argon2id não carregou. Recarregue a página; se persistir, seu navegador pode estar bloqueando WebAssembly.',
      input: 'Alguma entrada está fora do permitido.',
      unknown: 'Erro inesperado durante a derivação.',
    },

    result: {
      title: 'Sua senha',
      reveal: 'Revelar senha',
      hide: 'Ocultar senha',
      copy: 'Copiar',
      copied: 'Copiada!',
      copyFailed: 'Não consegui copiar — selecione e copie manualmente.',
      clipboardNote:
        'Tentamos limpar a área de transferência em 30 segundos — nem todo navegador permite.',
      elapsed: (ms: number) => `derivada em ${(ms / 1000).toFixed(1)} s`,
      entropy: (bits: number) => `${Math.round(bits)} bits de entropia`,
      strength: {
        weak: 'Fraca',
        fair: 'Razoável',
        good: 'Forte',
        excellent: 'Excelente',
      },
      recipe: 'Copiar link com os parâmetros',
      recipeCopied: 'Link copiado!',
      recipeNote:
        'O link guarda formato, versão e serviço — nunca a senha-mestra. Ele revela quais serviços você usa, então guarde-o em lugar privado.',
      stale: 'Os parâmetros mudaram. Gere de novo para ver a senha correspondente.',
    },
  },

  how: {
    title: 'Como funciona',
    subtitle:
      'Três entradas entram, uma senha sai. Sempre a mesma — e nada precisa ser guardado no caminho.',
    steps: [
      {
        title: 'Você informa o que só você sabe',
        body: 'A senha-mestra é o único segredo. Serviço, login e versão são dados públicos que apenas identificam a conta.',
      },
      {
        title: 'O navegador deriva a chave',
        body: 'Serviço, login, versão e formato viram um salt (SHA-256). Argon2id combina esse salt com a senha-mestra gastando 64 MiB de memória e produz 128 bytes.',
      },
      {
        title: 'Os bytes viram uma senha',
        body: 'Os 128 bytes são lidos como um número gigante e convertidos para o alfabeto que você escolheu, garantindo ao menos um caractere de cada tipo exigido.',
      },
    ],
    diagram: {
      title: 'Fluxo da derivação',
      master: 'senha-mestra',
      secret: 'segredo',
      public: 'público',
      service: 'serviço + login + versão + formato',
      sha: 'SHA-256',
      salt: 'salt (32 bytes)',
      argon: 'Argon2id',
      argonParams: '64 MiB · 3 passagens',
      key: '128 bytes',
      map: 'mapeamento',
      password: 'senha',
      caption:
        'Nenhuma seta sai do seu aparelho. Todo o diagrama acontece dentro da aba do navegador.',
    },
  },

  sec: {
    title: 'O que isso protege — e o que não protege',
    subtitle:
      'Nenhuma ferramenta de segurança é honesta se só lista as próprias virtudes.',
    protectsTitle: 'Protege você de',
    protects: [
      {
        title: 'Vazamento de cofre',
        body: 'Não existe arquivo de senhas para vazar, nem backup para perder, nem sincronização para interceptar.',
      },
      {
        title: 'Reuso de senha',
        body: 'Cada serviço recebe uma senha independente. Um vazamento no site A não diz nada sobre o site B.',
      },
      {
        title: 'Força bruta em GPU',
        body: 'Argon2id exige 64 MiB por tentativa. Isso derruba o paralelismo massivo que quebra hashes rápidos.',
      },
      {
        title: 'Dependência de nós',
        body: 'O algoritmo está documentado e tem implementação de referência em 200 linhas. Se este site sumir, suas senhas continuam recuperáveis.',
      },
    ],
    limitsTitle: 'Não protege você de',
    limits: [
      {
        title: 'Senha-mestra fraca',
        body: 'Argon2id encarece cada palpite, mas não salva uma mestra que está numa lista de senhas vazadas. Use uma frase longa e única.',
      },
      {
        title: 'Aparelho comprometido',
        body: 'Keylogger, malware ou extensão maliciosa leem a mestra enquanto você digita. Derivação nenhuma resolve isso.',
      },
      {
        title: 'Esquecer os parâmetros',
        body: 'Mudou o comprimento ou a versão e não anotou? A senha muda. Anote a receita ou use sempre o formato padrão.',
      },
      {
        title: 'Sites que forçam troca',
        body: 'Alguns exigem trocar a senha periodicamente. Use o campo Versão — mas lembre-se de qual número está valendo.',
      },
    ],
  },

  compat: {
    title: 'Na prática',
    subtitle: 'Dicas para o mundo real, onde os formulários nem sempre colaboram.',
    tips: [
      {
        title: 'O site recusou a senha',
        body: 'Use o formato Compatível: 12 caracteres, sem símbolos e sem caracteres ambíguos passa em quase todo validador teimoso.',
      },
      {
        title: 'Precisa trocar a senha',
        body: 'Aumente a Versão de 1 para 2. A senha muda por completo e você não precisa mexer na senha-mestra.',
      },
      {
        title: 'Vai digitar na TV ou no console',
        body: 'O formato Só letras gera 20 minúsculas: ainda são ~94 bits de entropia, mas sem caçar símbolos no teclado virtual.',
      },
      {
        title: 'Sem internet',
        body: 'Depois da primeira visita, a página funciona offline. Instale como aplicativo e ela fica disponível em modo avião.',
      },
      {
        title: 'Migrando aos poucos',
        body: 'Não troque tudo de uma vez. Comece pelas contas críticas — e-mail e banco — e vá convertendo as demais conforme usar.',
      },
      {
        title: 'Guarde a receita, não a senha',
        body: 'Anote serviço, login, versão e formato num papel ou nota. Nada disso é secreto, e é o suficiente para regerar tudo.',
      },
    ],
  },

  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        q: 'Se eu esquecer a senha-mestra, o que acontece?',
        a: 'Você perde o acesso a todas as senhas derivadas, e não há recuperação — não existe servidor nem cópia. Essa é a contrapartida direta de não guardar nada. Escolha uma frase memorável e, se fizer sentido para você, guarde uma cópia física em local seguro.',
      },
      {
        q: 'Isso é mais seguro que um gerenciador de senhas tradicional?',
        a: 'É um compromisso diferente, não estritamente melhor. Você troca o risco de vazamento do cofre pela exigência de lembrar os parâmetros e pela impossibilidade de trocar uma senha isolada sem usar o campo Versão. Gerenciadores tradicionais também guardam metadados úteis, como perguntas de segurança e códigos de recuperação, que este método não cobre.',
      },
      {
        q: 'Alguma coisa é enviada para um servidor?',
        a: 'Não. A página é um arquivo estático servido pelo GitHub Pages e não faz nenhuma requisição depois de carregar — sem analytics, sem CDN, sem fontes externas. Você pode conferir na aba Rede do navegador, ou desligar a internet e usar mesmo assim.',
      },
      {
        q: 'Por que o botão demora um ou dois segundos?',
        a: 'A lentidão é o recurso de segurança. Argon2id gasta 64 MiB de memória e três passagens para cada derivação. Para você é uma vez; para quem tenta adivinhar sua mestra, é esse custo multiplicado por bilhões de tentativas.',
      },
      {
        q: 'Posso usar em vários aparelhos?',
        a: 'Sim, e é justamente a graça: o mesmo algoritmo com as mesmas entradas produz a mesma senha no celular, no notebook e no navegador de um terceiro. Não há nada para sincronizar.',
      },
      {
        q: 'Por que a senha muda quando eu mexo no comprimento?',
        a: 'O formato entra no salt de propósito. Assim, variantes da mesma conta ficam matematicamente independentes: quem obtiver a versão de 12 caracteres não ganha nenhuma pista sobre a de 32.',
      },
      {
        q: 'Como sei que o código faz o que promete?',
        a: 'Todo o núcleo criptográfico cabe em cinco arquivos pequenos, a especificação está escrita em português e existe uma implementação de referência em Node com zero dependências. A suíte de testes compara as duas implementações caractere a caractere.',
      },
      {
        q: 'O que é a impressão digital ao lado da senha-mestra?',
        a: 'Três símbolos derivados só da sua mestra. Como eles aparecem antes de você gerar qualquer coisa, servem para pegar um erro de digitação na hora. Em compensação, expõem 24 bits de um hash rápido da mestra — por isso não devem ser fotografados nem compartilhados, e podem ser desligados.',
      },
    ],
  },

  footer: {
    spec: 'Especificação do algoritmo',
    code: 'Código-fonte',
    reference: 'Implementação de referência',
    license: 'Licença MIT',
    disclaimer:
      'Ferramenta livre, fornecida como está, sem garantias. Revise o código antes de confiar suas contas a ela.',
    offline: 'Funciona offline',
  },
}

/**
 * O dicionário em português é a fonte de verdade: o inglês precisa satisfazer
 * exatamente esta forma, então uma chave nova quebra o build até ser traduzida.
 */
export type Dictionary = typeof pt
